/* THEODORE films: each screen gets the file made for the frame it plays in.

   A film that has been re-encoded carries its renditions in
   data-td-renditions (snippets/theodore-film-renditions.liquid, written by
   tools/video/encode-films.py): centred crops of the original upload, each
   at one or more sizes and each in AV1 and HEVC. The frame decides which
   file plays. Films fill their frames with object-fit: cover, so a
   phone's portrait frame only ever shows a narrow centre slice of a
   landscape film; it is sent that slice, at the original's own sharpness,
   instead of the whole picture. A 21:9 banner is sent a 21:9 strip, and a
   1x screen fewer pixels than a 2x one.

   A rendition qualifies when its crop holds everything the frame shows and
   it has at least as many pixels across that view as the screen has device
   pixels (or, when none has, is the sharpest that holds the view); of
   those, the smallest file plays, in AV1 or HEVC, whichever of the two
   this device decodes in hardware at that size gives the smaller file (a
   desktop also takes one decoded in software when the browser says
   playback is smooth). A device that decodes neither that way plays
   the film's original URL (data-td-fallback), the file every device played
   before, so nobody gets less than they had. So does a frame the
   renditions were not cut for, and a film whose file fails to play keeps
   the original for the rest of the visit.

   A film is chosen once it has a size: one hidden with display: none (the
   banners hide theirs for reduced motion) is never fetched. It is chosen
   again when its frame grows past what the file covers or holds sharply (a
   phone turned on its side, a window widened), keeping its place in the
   loop. A smaller frame keeps the file it has.

   A film shown whole (object-fit other than cover: an article's film) can
   only take renditions of the whole picture, sized to what the frame shows;
   one with controls is sized for the screen, since it can go full screen.

   Films marked data-td-film-manual are placed only when their own script
   calls tdFilm.place() (the city cards wait for the load event); the rest
   are placed as soon as this runs. Loaded async from
   snippets/theodore-film.liquid; it scans again at DOMContentLoaded for
   films parsed after it ran, and after a theme-editor section reload.
   theodore-motion.js plays and pauses the films with the viewport and marks
   the ones on screen with data-td-want, so a film placed while on screen
   starts at once. The chosen file is named in data-td-film. */
(function () {
  'use strict';
  if (window.tdFilm) {
    window.tdFilm.scan(document);
    return;
  }

  var ORDER = ['av1', 'hevc'];
  var coarse = window.matchMedia('(pointer: coarse)').matches;
  var caps = {};
  var watched = [];
  var ro = window.ResizeObserver
    ? new window.ResizeObserver(function (entries) {
        entries.forEach(function (e) { place(e.target); });
      })
    : null;

  function manifest(v) {
    if (v._tdFilm === undefined) {
      try {
        v._tdFilm = JSON.parse(v.getAttribute('data-td-renditions'));
      } catch (e) {
        v._tdFilm = null;
      }
    }
    return v._tdFilm;
  }

  /* What the device makes of one file: { supported, smooth, powerEfficient }
     or null. Without the Media Capabilities API there is no way to ask about
     hardware, so AV1 is left out and HEVC is taken only where the browser
     is sure of it. */
  function decodes(codec, r, fps) {
    var s = r.s[codec];
    if (!s) return Promise.resolve(null);
    var key = s[1] + ' ' + r.w + 'x' + r.h;
    if (!caps[key]) {
      var type = 'video/mp4; codecs="' + s[1] + '"';
      var mc = navigator.mediaCapabilities;
      if (!mc || !mc.decodingInfo) {
        var sure = codec !== 'av1' && document.createElement('video').canPlayType(type) === 'probably';
        caps[key] = Promise.resolve(sure ? { supported: true, smooth: true, powerEfficient: true } : null);
      } else {
        caps[key] = mc
          .decodingInfo({ type: 'file', video: { contentType: type, width: r.w, height: r.h, bitrate: s[2], framerate: fps } })
          .then(
            function (info) { return info && info.supported ? info : null; },
            function () { return null; }
          );
      }
    }
    return caps[key];
  }

  /* The codecs this device plays a rendition in: a phone or tablet (coarse
     pointer) only those its hardware decodes, for the battery; a desktop any
     that plays smoothly, in hardware or not. None: the original plays. */
  function codecsFor(m, r) {
    return Promise.all(ORDER.map(function (c) { return decodes(c, r, m.fps); })).then(function (res) {
      return ORDER.filter(function (c, i) {
        return res[i] && (res[i].powerEfficient || (!coarse && res[i].smooth));
      });
    });
  }

  /* The part of the original the frame shows, in the original's pixels,
     and how many device pixels each of those is drawn across. */
  function view(v, m) {
    var b = v.getBoundingClientRect();
    if (!b.width || !b.height) return null;
    var cs = getComputedStyle(v);
    var dpr = window.devicePixelRatio || 1;
    var A = m.W / m.H;
    if (cs.objectFit !== 'cover') {
      /* The whole picture shows, fitted into the frame (an article's film).
         One with controls can be played full screen, so it is sized for the
         screen held sideways: going full screen must never look softer than
         the file it replaces. */
      var bw = b.width;
      var bh = b.height;
      if (v.controls && window.screen) {
        bw = Math.max(window.screen.width, window.screen.height);
        bh = Math.min(window.screen.width, window.screen.height);
      }
      var shown = cs.objectFit === 'fill' ? bw : Math.min(bw, bh * A);
      return { w: m.W, h: m.H, need: (shown * dpr) / m.W, centred: false };
    }
    var B = b.width / b.height;
    var w = B <= A ? B * m.H : m.W;
    return {
      w: w,
      h: B <= A ? m.H : m.W / B,
      need: (b.width * dpr) / w,
      centred: cs.objectPosition === '50% 50%',
    };
  }

  function holds(r, m, vw) {
    if (!vw.centred && !(r.cw === m.W && r.ch === m.H)) return false;
    return r.cw + 1 >= vw.w && r.ch + 1 >= vw.h;
  }

  function sharpEnough(d, vw) {
    return d >= vw.need * 0.97;
  }

  /* The renditions that may play in this view: those that hold it and are
     sharp enough, or, when none is, the sharpest that hold it, within the
     same 3% that counts as sharp enough (a 1992-pixel cut of a 2000-pixel
     crop is as sharp as the 2000-pixel whole picture to anyone looking).
     top is the sharpest density any of them holds the view at. */
  function qualifying(m, vw, od) {
    var hold = m.r.filter(function (r) { return holds(r, m, vw); });
    if (!hold.length) return null;
    var top = Math.max.apply(null, hold.map(function (r) { return r.w / r.cw; }));
    var sharp = top >= vw.need * 0.97;
    /* one 3% rule for every comparison: within 3% of what the screen needs,
       or, when nothing is that sharp, of the sharpest file or the original,
       whichever is sharper (so a file is never chosen more than 3% softer
       than the file it replaces, and a 0.3%-softer full match is never
       passed over for an allowance file) */
    var floor = (sharp ? Math.min(vw.need, top) : Math.max(top, od || 0)) * 0.97;
    return {
      pool: hold.filter(function (r) { return r.w / r.cw >= floor; }),
      top: top,
      floor: floor,
      sharp: sharp,
    };
  }

  /* How sharp the original is, from its Shopify URL (…SD-480p…, …HD-1080p…:
     rows of the whole picture); 0 when the URL does not say. */
  function originalDensity(v, m) {
    var rows = /[-.](\d{3,4})p[-.]/.exec(v.getAttribute('data-td-fallback') || '');
    return rows ? Number(rows[1]) / m.H : 0;
  }

  function play(v) {
    var p = v.play();
    if (p && p.catch) p.catch(function () {});
  }

  /* one file named two ways: an absolute currentSrc against a //host or
     /path URL from the markup */
  function sameFile(a, b) {
    var path = function (u) { return String(u).replace(/^[a-z]+:/i, '').replace(/^\/\/[^/]+/, ''); };
    return path(a) === path(b);
  }

  function swap(v, c) {
    if (v._tdChoice && v._tdChoice.url === c.url) return;
    /* the file it has: a src, or the <source> an article's film was given in
       its markup (upgradeBody takes that element out, but a visitor may have
       pressed play on it before this script ran) */
    var loaded = v.getAttribute('src') || v.currentSrc || '';
    var hadFile = !!loaded;
    if (hadFile && !v._tdChoice && sameFile(loaded, c.url)) {
      /* the choice is the file it already has: setting it again would fetch
         it again and send a playing film back to the start */
      v._tdChoice = c;
      v.setAttribute('data-td-film', c.r ? c.r.w + 'x' + c.r.h + ' ' + c.codec : 'original');
      return;
    }
    var playing = hadFile && !v.paused;
    var t = v.currentTime;
    /* autoplay is for the first file only: a new src re-arms it, which would
       start (and download) a film theodore-motion.js has paused off screen */
    if (hadFile) v.autoplay = false;
    v._tdChoice = c;
    v.setAttribute('data-td-film', c.r ? c.r.w + 'x' + c.r.h + ' ' + c.codec : 'original');
    if (hadFile && t) {
      v.addEventListener(
        'loadedmetadata',
        function () {
          try {
            v.currentTime = v.duration ? t % v.duration : t;
          } catch (e) {}
        },
        { once: true }
      );
    }
    v.src = c.url;
    if (playing || v.hasAttribute('data-td-want')) play(v);
    else if (hadFile) v.pause();
  }

  function original(v) {
    v._tdTicket = (v._tdTicket || 0) + 1;
    var fb = v.getAttribute('data-td-fallback');
    if (fb) swap(v, { r: null, url: fb, d: 0, codec: null });
  }

  function place(v) {
    if (v._tdBroken) return;
    /* every decision supersedes one still waiting on the decoder question,
       including a decision to keep the file playing */
    var ticket = (v._tdTicket = (v._tdTicket || 0) + 1);
    var m = manifest(v);
    if (!m || !m.r || !m.r.length) {
      /* a manifest that does not parse: the film plays as it did before */
      if (v.getBoundingClientRect().width) original(v);
      return;
    }
    var vw = view(v, m);
    if (!vw) return;
    var od = originalDensity(v, m);
    var q = qualifying(m, vw, od);
    /* no rendition is sharp enough for this screen: if the original is more
       than 3% sharper than the best of them, it plays (the files a film has
       are cut never to be softer than it, but this keeps a film published
       only in part, or a frame they were not cut for, from getting less) */
    if (q && !q.sharp && od * 0.97 > q.top) {
      if (!v._tdChoice || v._tdChoice.r) original(v);
      return;
    }
    var cur = v._tdChoice;
    if (cur) {
      if (q && q.pool.indexOf(cur.r) >= 0) return;
      /* keep the file playing unless the frame now shows more than it
         holds, or needs it sharper than it is and a sharper one exists
         (the original is the whole picture, so it always holds) */
      var curHolds = cur.r ? holds(cur.r, m, vw) : true;
      if (curHolds && (!q || cur.d >= q.floor)) return;
    }
    if (!q) {
      original(v);
      return;
    }
    /* every candidate in every codec this device plays well; the smallest
       file wins (neither codec is always the smaller: matching the old file
       on the hero's phone crop took 2.7 MB in AV1 and 1.9 MB in HEVC) */
    Promise.all(q.pool.map(function (r) {
      return codecsFor(m, r).then(function (cs) {
        return cs.map(function (c) { return { r: r, codec: c, bytes: r.s[c][3], saving: r.s[c][4] || 0 }; });
      });
    })).then(function (lists) {
      if (ticket !== v._tdTicket) return;
      var opts = [].concat.apply([], lists);
      if (!opts.length) {
        original(v);
        return;
      }
      var smaller = function (a, o) { return o.bytes < a.bytes ? o : a; };
      /* a file that matched the old one only with the allowance (one-second
         stretches a little below it; s.<codec>[4] is what it must save)
         plays only where it saves that much over the smallest file that
         matched it in full, or where no such file plays (the encoder kept it
         only at that much smaller than the old file) */
      var full = opts.filter(function (o) { return !o.saving; });
      var least = full.length ? full.reduce(smaller).bytes : Infinity;
      opts = opts.filter(function (o) { return !o.saving || o.bytes <= least - o.saving; });
      var b = opts.reduce(smaller);
      swap(v, { r: b.r, url: (m.b || '') + b.r.s[b.codec][0], d: b.r.w / b.r.cw, codec: b.codec });
    });
  }

  /* A file the device turns out not to play (a decoder that promised more
     than it does): the original, for good, so a resize does not try the
     broken file again. */
  function failed(v) {
    var cur = v._tdChoice;
    if (!cur || !cur.r) return;
    v._tdBroken = true;
    original(v);
  }

  /* A window dragged to a sharper screen changes devicePixelRatio without
     resizing anything, so the ResizeObserver stays quiet: a resolution query
     for the current ratio fires once when it stops matching, and is set up
     again for the new ratio. */
  function watchRatio() {
    var mq = window.matchMedia('(resolution: ' + (window.devicePixelRatio || 1) + 'dppx)');
    function changed() {
      if (mq.removeEventListener) mq.removeEventListener('change', changed);
      else if (mq.removeListener) mq.removeListener(changed);
      watched.forEach(place);
      watchRatio();
    }
    if (mq.addEventListener) mq.addEventListener('change', changed);
    else if (mq.addListener) mq.addListener(changed);
  }

  function watch(v) {
    if (v._tdWatched) return;
    v._tdWatched = true;
    watched.push(v);
    v.addEventListener('error', function () { failed(v); });
    if (ro) ro.observe(v);
    place(v);
  }

  /* Films pasted into an article's text: a <video> with a <source> in the
     body HTML. snippets/theodore-film-body.liquid lists, by Shopify video id,
     the renditions of the films that article holds; a video whose source is
     one of them gets them here (its <source> goes, its URL becomes the
     fallback), and is then chosen for like any other film. */
  function upgradeBody(root) {
    var lists = (root || document).querySelectorAll('script[data-td-film-body]');
    Array.prototype.forEach.call(lists, function (list) {
      var films;
      try {
        films = JSON.parse(list.textContent);
      } catch (e) {
        return;
      }
      var scope = list.parentNode || document;
      Array.prototype.forEach.call(scope.querySelectorAll('video:not([data-td-renditions])'), function (v) {
        var source = v.querySelector('source[src]');
        var url = v.getAttribute('src') || (source && source.getAttribute('src')) || '';
        Object.keys(films).forEach(function (id) {
          if (!url || url.indexOf(id) < 0 || v.hasAttribute('data-td-renditions')) return;
          v.setAttribute('data-td-renditions', JSON.stringify(films[id]));
          v.setAttribute('data-td-fallback', url);
          Array.prototype.forEach.call(v.querySelectorAll('source'), function (el) { el.remove(); });
        });
      });
    });
  }

  function scan(root) {
    upgradeBody(root);
    var vids = (root || document).querySelectorAll('video[data-td-renditions]:not([data-td-film-manual])');
    Array.prototype.forEach.call(vids, watch);
  }

  window.tdFilm = {
    scan: scan,
    place: function (v) {
      if (v._tdWatched) place(v);
      else watch(v);
    },
  };

  watchRatio();
  scan(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(document); });
  }
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
})();
